import { FC, useEffect, useRef, useState } from "react";
import { useMobile } from "../MobileProvider";
import { AnimatePresence, motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useApp } from "../AppProvider";
import styled from "@emotion/styled";
import { logEvent } from "@firebase/analytics";
import { getDownloadURL, ref as firestoreRef } from "@firebase/storage";

const ProjectStyled = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== "large",
})<{ large?: boolean }>(({ large }) => ({
  width: "100%",
  height: 300,
  borderRadius: 8,
  cursor: "pointer",
  gridColumn: "span 1",
  background: "gray",
  position: "relative",
  willChange: "transform",
  transitionProperty: "border-radius",
  transitionDuration: "0.2s",
  transitionTimingFunction: "ease-in-out",
  transitionDelay: "0.2s",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "@media (min-width: 768px)": {
    ...(large && {
      gridColumn: "span 2",
    }),
  },
  "@media (min-width: 1920px)": {
    ...(large && {
      gridColumn: "span 3",
    }),
  },
}));

const Background = styled(motion.img)(() => ({
  backgroundRepeat: "no-repeat",
  willChange: "transform",
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "cover",
  height: "100%",
}));

const ProjectMarquee = styled(motion.div)(() => ({
  position: "absolute",
  willChange: "transform",
  width: "100%",
  maxWidth: "100%",
  height: 200,
  overflow: "hidden",
  top: "50%",
  left: 0,
  display: "flex",
  flexDirection: "row",
}));

const Title = styled("h1")(() => ({
  margin: "20px 0",
  fontSize: "2rem",
  color: "white",
  paddingLeft: "4rem",
  textTransform: "uppercase",
  webkitTextFillColor: "rgba(255,255,255,0)",
  webkitTextStrokeWidth: 2,
  webkitTextStrokeColor: "white",
}));

const MobileTitle = styled("h1")(() => ({
  position: "absolute",
  top: "50%",
  left: 0,
  transform: "translateY(-50%)",
  color: "#fff",
  width: "100%",
  textAlign: "center",
  margin: 0,
  marginBlockEnd: 0,
  marginBlockStart: 0,
  paddingLeft: 20,
  paddingRight: 20,
}));

const BackgroundLayer = styled("div")(() => ({
  width: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.3)",
}));

type Props = {
  image: Object;
  title: string;
  url: string;
  size: string;
};

const Projekt: FC<Props> = (props) => {
  const { storage, setModalVideoUrl, analytics } = useApp();
  const mobile = useMobile();
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancel = false;

    if (storage && url === undefined) {
      getDownloadURL(firestoreRef(storage, `projekte/${props.image}`)).then(
        (newUrl) => {
          if (!cancel) {
            setUrl(newUrl);
          }
        }
      );
    }

    return () => {
      cancel = true;
    };
  }, [props.image, storage, url]);

  useEffect(() => {
    if (mobile) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [mobile]);

  const handleMouseEnter = () => {
    setShow(true);
    if (analytics) {
      logEvent(analytics, "hover_projekt_" + props.title);
    }
  };

  const handleClick = () => {
    setModalVideoUrl(props.url);
    if (analytics) {
      logEvent(analytics, "click_projekt_" + props.title);
    }
  };

  return (
    <ProjectStyled
      ref={ref}
      initial={{ y: "20%", opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
      onClick={handleClick}
      large={props.size === "large"}
    >
      <Background
        initial={{ scale: 1 }}
        animate={{ scale: show ? 1.05 : 1 }}
        transition={{
          duration: 0.4,
          stiffness: 100,
          damping: 20,
          type: "spring",
        }}
        src={url}
        loading={"lazy"}
        alt={props.title}
      />
      {mobile && (
        <>
          <BackgroundLayer />
          <MobileTitle>{props.title}</MobileTitle>
        </>
      )}
      <AnimatePresence>
        {show && !mobile && (
          <ProjectMarquee
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: "-50%" }}
            exit={{ opacity: 0, y: 0 }}
            transition={{
              duration: 0.4,
              delay: mobile ? 0.3 : 0,
              type: "spring",
              stiffness: 100,
              damping: 20,
            }}
          >
            <Marquee gradient={false} speed={60}>
              <Title>{props.title}</Title>
            </Marquee>
          </ProjectMarquee>
        )}
      </AnimatePresence>
    </ProjectStyled>
  );
};

export default Projekt;
